// React bootstrap table next =>
// DOCS: https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/
// STORYBOOK: https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { shallowEqual, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider } from "react-bootstrap-table2-paginator";

import * as actions from "../../../_redux/projects/projectsActions";
import * as uiHelpers from "./ProjectFilesUIHelper";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { DocumentTypeColumnFormatter } from "../../../../Common/DocumentTypeColumnFormatter";
import {
  ActionsColumnFormatter,
  UploadedDateTimeColumnFormatter,
} from "../../../../../_components/column-formatters";
import {
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
} from "../../../../../../_metronic/_helpers";
import { useProjectFilesUIContext } from "./ProjectFilesUIContext";
import { useEntityDeleteDialogContext } from "../../entity-delete-dialog/EntityDeleteDialogContext";
import { PROJECT_PRODUCT_FILE_TYPES } from "../../../../../_utils/listUtils";
import { canEdit, isAdmin } from "../../../../../_utils/authUtils";
import { USER } from "../../../../../_utils/userTypes";
import { displayColumnIsConfidential, sortDateFunc } from "../../../../../_utils/columnUtils";
import { useSearch } from "../../../../../hooks/useSearch";
import { columnTags } from "../../../../../_utils/tagsUtils";
import { useFileViewer } from "../../../../../hooks/useFileViewer";

export function ProjectFilesTable() {
  const intl = useIntl();
  const filesUIContext = useProjectFilesUIContext();
  const { setDeleteEntityDialog } = useEntityDeleteDialogContext();
  const { setEntities, entitiesFiltered } = useSearch();
  const { downloadAndOpenFilesInViewer } = useFileViewer();

  const filesUIProps = useMemo(() => {
    return {
      ids: filesUIContext.ids,
      setIds: filesUIContext.setIds,
      filters: filesUIContext.filters,
      setFilters: filesUIContext.setFilters,
      projectId: filesUIContext.projectId,
      openRequestSignatureDialog: filesUIContext.openRequestSignatureDialog,
      openEditFileDialog: filesUIContext.openEditFileDialog,
      readOnly: filesUIContext.readOnly,
      context: filesUIContext.context,
      filterList: filesUIContext.filterList,
    };
  }, [filesUIContext]);

  const { totalCount, entities, userId, groups, session } = useSelector(
    (state) => ({
      totalCount: state.projects?.projectForEdit?.current?.files?.length,
      entities:
        filesUIProps.context === "lead"
          ? state?.leads?.leadForEdit?.saved?.project?.files
          : state.projects?.projectForEdit?.current?.files,
      userId: state.auth.session.id,
      groups: state.auth.groups,
      session: state.auth.session,
    }),
    shallowEqual
  );

  const openDeleteFileDialog = (id) => {
    setDeleteEntityDialog({
      action: { fn: actions.deleteFile, props: { id, projectId: filesUIProps.projectId } },
      entityType: "PROJECT.FILE",
    });
  };
  useEffect(() => {
    setEntities(entities);
  }, [entities]);

  const columns = [
    {
      dataField: "id",
      text: "",
      sort: false,
      formatter: DocumentTypeColumnFormatter,
    },

    {
      dataField: "friendlyName",
      text: intl.formatMessage({
        id: "COMMON.NAME",
      }),
      sort: true,
      sortCaret: sortCaret,
      headerClasses: "text-left text-nowrap",
      classes: "text-left",
    },

    {
      dataField: "fileType",
      text: intl.formatMessage({
        id: "COMMON.TYPE",
      }),
      sort: true,
      sortCaret: sortCaret,
      headerClasses: "text-left text-nowrap",
      classes: "text-left",
      formatter: (cell) => (
        <span>{intl.formatMessage({ id: PROJECT_PRODUCT_FILE_TYPES[cell] })}</span>
      ),
      sortValue: (cell) => intl.formatMessage({ id: PROJECT_PRODUCT_FILE_TYPES[cell ?? "-"] }),
    },

    {
      dataField: "uploadedAt",
      text: intl.formatMessage({
        id: "COMMON.UPLOADED.DATE",
      }),
      sort: true,
      sortCaret: sortCaret,
      formatter: UploadedDateTimeColumnFormatter,
      formatExtraData: {
        readOnly: filesUIProps.readOnly,
        userId,
        intl,
      },
      sortFunc: sortDateFunc,
      headerClasses: "text-left text-nowrap",
      classes: "text-left",
    },

    columnTags(groups, session, intl),

    {
      dataField: "action",
      text: intl.formatMessage({
        id: "ACTION.TITLE",
      }),
      formatter: ActionsColumnFormatter,
      formatExtraData: {
        openEditDialog: filesUIProps.openEditFileDialog,
        openRequestSignatureDialog: filesUIProps.openRequestSignatureDialog,
        openDeleteDialog: openDeleteFileDialog,
        actionList: ["SIGNATURE", "WRITE", "TRASH"],
        canRequestSignature: () => {
          return isAdmin(groups, session) || session?.userTypes?.includes(USER);
        },
        canDelete: (row) => {
          return canEdit(groups, session, "PROJECT");
        },
        canEdit: (row) => {
          return canEdit(groups, session, "PROJECT");
        },
        entityType: "FILE",
      },
      classes: "text-right",
      headerClasses: "text-right",
      style: {
        minWidth: "100px",
      },
      hidden: filesUIProps.readOnly,
    },
  ];

  displayColumnIsConfidential({ entitiesFiltered, columns });

  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: filesUIProps.filters.pageSize,
    page: filesUIProps.filters.pageNumber,
  };

  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination paginationProps={paginationProps}>
            <BootstrapTable
              data-locale={intl.locale}
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center"
              bordered={false}
              bootstrap4
              keyField="id"
              data={entitiesFiltered}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              hover
              rowEvents={{
                onClick: (e, fileRowItem, rowIndex) => {
                  downloadAndOpenFilesInViewer([fileRowItem]);
                },
              }}
              rowStyle={{ cursor: "pointer" }}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );
      }}
    </PaginationProvider>
  );
}
